import { decode } from 'html-entities'
import { decodeAllStrings } from '@utils/decoder'
import { trim } from 'lodash-es'

/**
 * Loads JSON data from a DOM node with the given ID.
 * The data should be escaped to be safely embedded in the HTML.
 * We decode the data, parse it into JSON, and apply a second decoding pass
 * to handle any double-encoded entities.
 *
 * @param domNodeID - The ID of the DOM node containing the JSON string.
 * @returns The parsed and decoded JSON object, or an empty object if parsing fails.
 */
export function loadJSONFromDOMNode(domNodeID: string): Record<string, any> | null {
  const dataNode = document.getElementById(domNodeID)
  let parsedJSON: Record<string, any> = {}

  if (dataNode && dataNode.textContent) {
    const encoded = trim(dataNode.textContent)
    const decoded = decode(encoded)
    try {
      parsedJSON = JSON.parse(decoded)
      // The initial decoding step converts entities like &quot;, but may leave &amp;.
      // A second pass is applied to decode all strings in the parsed JSON object, thus
      // handling any double encoded strings
      const fullyDecoded = decodeAllStrings(parsedJSON)

      return fullyDecoded
    } catch (err: any) {
      if (err.name === 'SyntaxError') {
        console.warn(`SyntaxError: Tried to parse invalid JSON in DOM node "${domNodeID}". Content:`, decoded)
      } else {
        console.warn(`Error parsing JSON from DOM node "${domNodeID}":`, err)
      }
      console.warn('Returning an empty JSON object')
    }
  } else {
    console.warn('Failed to load JSON from DOM node with id', domNodeID)
  }

  return null
}
